/**
 * 관리자 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { adminService } from '../../services/adminService';
import { AdminUser, AdminUserFilter } from '../../types/admin';

const AdminsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AdminUserFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admins', filter],
    queryFn: () => adminService.getAdmins(filter),
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">활성</Badge>;
      case 'inactive':
        return <Badge bg="secondary">비활성</Badge>;
      case 'suspended':
        return <Badge bg="danger">정지</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'username',
      label: '사용자명',
      render: (admin) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/admins/${admin.id}`)}
        >
          {admin.username}
        </span>
      ),
    },
    {
      key: 'name',
      label: '이름',
      render: (admin) => (
        <span style={{ color: '#ccc' }}>{admin.name}</span>
      ),
    },
    {
      key: 'email',
      label: '이메일',
      render: (admin) => (
        <span style={{ color: '#ccc' }}>{admin.email}</span>
      ),
    },
    {
      key: 'phone',
      label: '연락처',
      render: (admin) => (
        <span style={{ color: '#ccc' }}>{admin.phone || '-'}</span>
      ),
    },
    {
      key: 'role',
      label: '권한',
      render: (admin: AdminUser) => (
        <span style={{ color: '#00BCD4' }}>{admin.role || '-'}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (admin) => getStatusBadge(admin.status),
    },
    {
      key: 'last_login_at',
      label: '최종 로그인',
      render: (admin) => (
        <span style={{ color: '#ccc' }}>
          {admin.last_login_at ? new Date(admin.last_login_at).toLocaleDateString('ko-KR') : '-'}
        </span>
      ),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '사용자명, 이름, 이메일로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
        { value: 'suspended', label: '정지' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (admin: AdminUser) => {
    navigate(`/admin/admins/${admin.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>관리자 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/admins/new')}>
            관리자 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination?.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.admins || []}
              loading={isLoading}
              emptyMessage="관리자가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminsList;

